import React, { useState, useMemo } from 'react';
import {
  DollarSign,
  ChevronRight,
  ChevronDown,
  Edit2,
  Loader2,
  GraduationCap,
  Layers,
  GitBranch,
  Building2,
} from 'lucide-react';
import type {
  TuitionGridWithRelations,
  TuitionGridSettings,
  TypeFormation,
  CampusInfo,
} from '@/types/tuition.types';
import type { FiliereWithOptions } from '@/types/filiere.types';
import { formatCurrency, formatNiveau, formatTypeFormation } from '@/services/tuitionService';

interface GroupedFiliere {
  id: string;
  filiere: string;
  codeFiliere: string;
  nombreAnnees: number;
  isActive: boolean;
  options: {
    id: string;
    codeFiliere: string;
    filiere: string;
    optionName: string | null;
    isActive: boolean;
  }[];
  // Pricing data
  grids: TuitionGridWithRelations[];
  isConfigured: boolean;
}

interface TuitionGridGroupedTableProps {
  filieres: FiliereWithOptions[];
  grids: TuitionGridWithRelations[];
  settings: TuitionGridSettings | null;
  campusInfo?: CampusInfo | null;
  selectedCampusId?: string; // Currently selected campus filter (undefined = all campuses)
  isLoading: boolean;
  canManage?: boolean; // Whether user can edit (hides Actions column if false)
  onEdit: (filiereId: string, niveau?: number, typeFormation?: TypeFormation) => void;
  onConfigureFiliere: (filiereId: string) => void;
}

const TuitionGridGroupedTable: React.FC<TuitionGridGroupedTableProps> = ({
  filieres,
  grids,
  settings,
  campusInfo,
  selectedCampusId,
  isLoading,
  canManage = true,
  onEdit,
  onConfigureFiliere,
}) => {
  const [expandedFilieres, setExpandedFilieres] = useState<Set<string>>(new Set());
  const [expandedNiveaux, setExpandedNiveaux] = useState<Set<string>>(new Set());
  const [expandedCampuses, setExpandedCampuses] = useState<Set<string>>(new Set());

  // Determine pricing mode based on settings
  const differsByNiveau = settings?.differsByNiveau || false;
  const differsByTypeFormation = settings?.differsByTypeFormation || false;
  const differsByCampus = settings?.differsByCampus || false;

  // Check if we're showing all campuses (no specific campus filter)
  const showingAllCampuses = differsByCampus && campusInfo?.hasMultipleCampuses && !selectedCampusId;

  // Group grids by filière
  const groupedData = useMemo(() => {
    const grouped: GroupedFiliere[] = filieres.map((filiere) => {
      const filiereGrids = grids.filter((g) => g.filiereId === filiere.id);
      const isConfigured = filiereGrids.some((g) => g.tuitionAmount > 0 || g.registrationFee > 0);

      return {
        id: filiere.id,
        filiere: filiere.filiere,
        codeFiliere: filiere.codeFiliere,
        nombreAnnees: filiere.nombreAnnees,
        isActive: filiere.isActive,
        options: filiere.options,
        grids: filiereGrids,
        isConfigured,
      };
    });

    return grouped;
  }, [filieres, grids]);

  const toggleFiliere = (filiereId: string) => {
    setExpandedFilieres((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filiereId)) {
        newSet.delete(filiereId);
      } else {
        newSet.add(filiereId);
      }
      return newSet;
    });
  };

  const toggleNiveau = (key: string) => {
    setExpandedNiveaux((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleCampus = (key: string) => {
    setExpandedCampuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Get unique campuses from grids
  const getCampusesFromGrids = (gridsForFiliere: TuitionGridWithRelations[]) => {
    const campusMap = new Map<string, { id: string; name: string }>();
    gridsForFiliere.forEach((g) => {
      if (g.campusId && g.campus) {
        campusMap.set(g.campusId, { id: g.campusId, name: g.campus.name });
      }
    });
    return Array.from(campusMap.values());
  };

  // Get price for a filière (when no dimensions vary)
  const getSinglePrice = (grids: TuitionGridWithRelations[]) => {
    if (grids.length === 0) return null;
    // Find a grid with actual prices, or fallback to first grid
    const grid = grids.find((g) => g.tuitionAmount > 0 || g.registrationFee > 0) || grids[0];
    return {
      tuitionAmount: grid.tuitionAmount,
      registrationFee: grid.registrationFee,
    };
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  if (groupedData.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="text-center py-12">
          <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune filière</h3>
          <p className="mt-1 text-sm text-gray-500">
            Synchronisez les filières pour commencer la configuration des tarifs.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Filière
              </th>
              {/* Always show price columns - Inscription first, then Scolarité, then Total */}
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Inscription
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Scolarité
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Total
              </th>
              {canManage && (
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {groupedData.map((filiere) => {
              const isExpanded = expandedFilieres.has(filiere.id);
              const hasOptions = filiere.options.length > 0;
              const filiereCampuses = getCampusesFromGrids(filiere.grids);
              const hasCampusPrices = showingAllCampuses && filiereCampuses.length > 0;
              // Can expand if any pricing dimension varies OR has options OR showing campus breakdown
              const canExpand = (differsByNiveau || differsByTypeFormation || hasOptions || hasCampusPrices) && filiere.isConfigured;
              const singlePrice = !differsByNiveau && !differsByTypeFormation && !hasCampusPrices ? getSinglePrice(filiere.grids) : null;

              return (
                <React.Fragment key={filiere.id}>
                  {/* Parent Filière Row */}
                  <tr
                    className={`hover:bg-gray-50 transition-colors ${
                      !filiere.isActive ? 'bg-gray-50 opacity-60' : ''
                    } ${canExpand ? 'cursor-pointer' : ''}`}
                    onClick={() => canExpand && toggleFiliere(filiere.id)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* Expand/Collapse indicator */}
                        {canExpand ? (
                          <div className="w-6 h-6 flex items-center justify-center">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                          </div>
                        ) : (
                          <div className="w-6" />
                        )}
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {filiere.filiere}
                            {hasOptions && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                <GitBranch className="w-3 h-3" />
                                {filiere.options.length} option{filiere.options.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {filiere.codeFiliere} - {filiere.nombreAnnees} an{filiere.nombreAnnees > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Price columns - always show, but content varies based on settings */}
                    {!differsByNiveau && !differsByTypeFormation && !hasCampusPrices ? (
                      // Single price mode - show actual prices
                      <>
                        <td className="px-6 py-4 text-right">
                          {filiere.isConfigured && singlePrice ? (
                            <span className="text-gray-600">
                              {formatCurrency(singlePrice.registrationFee)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {filiere.isConfigured && singlePrice ? (
                            <span className="font-medium text-gray-900">
                              {formatCurrency(singlePrice.tuitionAmount)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {filiere.isConfigured && singlePrice ? (
                            <span className="font-semibold text-primary-600">
                              {formatCurrency(singlePrice.tuitionAmount + singlePrice.registrationFee)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </>
                    ) : (
                      // Dimensions vary - show configuration status spanning 3 columns
                      <td className="px-6 py-4 text-center" colSpan={3}>
                        {filiere.isConfigured ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            {hasCampusPrices ? <Building2 className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
                            Configuré - Cliquez pour voir les détails
                            {hasCampusPrices && ` (${filiereCampuses.length} campus)`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">
                            Non configuré
                          </span>
                        )}
                      </td>
                    )}

                    {/* Actions - only shown when canManage is true */}
                    {canManage && (
                      <td className="px-6 py-4 text-center">
                        <div
                          className="flex items-center justify-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {filiere.isConfigured ? (
                            <button
                              onClick={() => onEdit(filiere.id)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => onConfigureFiliere(filiere.id)}
                              className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors font-medium"
                            >
                              Configurer
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>

                  {/* Expanded content: Niveau rows (only when NOT showing campus breakdown) */}
                  {isExpanded && differsByNiveau && !hasCampusPrices && (
                    <>
                      {Array.from({ length: filiere.nombreAnnees }, (_, i) => i + 1).map((niveau) => {
                        const niveauGrids = filiere.grids.filter((g) => g.niveau === niveau);
                        const niveauKey = `${filiere.id}-${niveau}`;
                        const isNiveauExpanded = expandedNiveaux.has(niveauKey);
                        // Always expandable when type formation varies, regardless of grid count
                        const hasTypePrices = differsByTypeFormation;
                        const niveauPrice = niveauGrids.find((g) => g.tuitionAmount > 0 || g.registrationFee > 0) || niveauGrids[0];

                        return (
                          <React.Fragment key={niveauKey}>
                            <tr
                              className={`bg-gray-50/50 hover:bg-gray-100/50 transition-colors ${
                                hasTypePrices ? 'cursor-pointer' : ''
                              }`}
                              onClick={() => hasTypePrices && toggleNiveau(niveauKey)}
                            >
                              <td className="px-6 py-3">
                                <div className="flex items-center gap-3 pl-12">
                                  {hasTypePrices ? (
                                    <div className="w-5 h-5 flex items-center justify-center">
                                      {isNiveauExpanded ? (
                                        <ChevronDown className="w-3 h-3 text-gray-400" />
                                      ) : (
                                        <ChevronRight className="w-3 h-3 text-gray-400" />
                                      )}
                                    </div>
                                  ) : (
                                    <div className="w-5" />
                                  )}
                                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                    <Layers className="w-4 h-4 text-purple-600" />
                                  </div>
                                  <span className="font-medium text-gray-700 text-sm">
                                    {formatNiveau(niveau)}
                                  </span>
                                </div>
                              </td>
                              {/* When type formation varies, span the columns for expansion indicator */}
                              {differsByTypeFormation ? (
                                <td className="px-6 py-3 text-center text-sm text-gray-500" colSpan={3}>
                                  Cliquez pour voir les détails
                                </td>
                              ) : (
                                <>
                                  <td className="px-6 py-3 text-right text-sm">
                                    {niveauPrice ? (
                                      <span className="text-gray-600">
                                        {formatCurrency(niveauPrice.registrationFee)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-3 text-right text-sm">
                                    {niveauPrice ? (
                                      <span className="font-medium text-gray-900">
                                        {formatCurrency(niveauPrice.tuitionAmount)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-3 text-right text-sm">
                                    {niveauPrice ? (
                                      <span className="font-semibold text-primary-600">
                                        {formatCurrency(niveauPrice.tuitionAmount + niveauPrice.registrationFee)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </>
                              )}
                              {canManage && (
                                <td className="px-6 py-3 text-center">
                                  {/* Edit only at parent level */}
                                </td>
                              )}
                            </tr>

                            {/* Type Formation rows (nested under niveau) */}
                            {isNiveauExpanded && differsByTypeFormation && (
                              <>
                                {(['INITIALE', 'CONTINUE'] as TypeFormation[]).map((type) => {
                                  // Find grid with actual price, or fallback to any matching grid
                                  const typeGrid = niveauGrids.find((g) => g.typeFormation === type && (g.tuitionAmount > 0 || g.registrationFee > 0))
                                    || niveauGrids.find((g) => g.typeFormation === type);
                                  return (
                                    <tr
                                      key={`${niveauKey}-${type}`}
                                      className="bg-green-50/30 hover:bg-green-50/50 transition-colors"
                                    >
                                      <td className="px-6 py-2">
                                        <div className="flex items-center gap-3 pl-20">
                                          <div className="w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                                            <GraduationCap className="w-3 h-3 text-green-600" />
                                          </div>
                                          <span className="text-gray-600 text-sm">
                                            {formatTypeFormation(type)}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-6 py-2 text-right text-sm">
                                        {typeGrid ? (
                                          <span className="text-gray-600">
                                            {formatCurrency(typeGrid.registrationFee)}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-6 py-2 text-right text-sm">
                                        {typeGrid ? (
                                          <span className="font-medium text-gray-900">
                                            {formatCurrency(typeGrid.tuitionAmount)}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-6 py-2 text-right text-sm">
                                        {typeGrid ? (
                                          <span className="font-semibold text-primary-600">
                                            {formatCurrency(typeGrid.tuitionAmount + typeGrid.registrationFee)}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      {canManage && (
                                        <td className="px-6 py-2 text-center">
                                          {/* Edit only at parent level */}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}

                  {/* Expanded content: Type Formation rows (when ONLY type varies, not niveau, not campus) */}
                  {isExpanded && differsByTypeFormation && !differsByNiveau && !hasCampusPrices && (
                    <>
                      {(['INITIALE', 'CONTINUE'] as TypeFormation[]).map((type) => {
                        // Find grid with actual price, or fallback to any matching grid
                        const typeGrids = filiere.grids.filter((g) => g.typeFormation === type);
                        const typeGrid = typeGrids.find((g) => g.tuitionAmount > 0 || g.registrationFee > 0) || typeGrids[0];
                        return (
                          <tr
                            key={`${filiere.id}-${type}`}
                            className="bg-green-50/30 hover:bg-green-50/50 transition-colors"
                          >
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-3 pl-12">
                                <div className="w-5" />
                                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                                  <GraduationCap className="w-4 h-4 text-green-600" />
                                </div>
                                <span className="font-medium text-gray-700 text-sm">
                                  {formatTypeFormation(type)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-3 text-right text-sm">
                              {typeGrid ? (
                                <span className="text-gray-600">
                                  {formatCurrency(typeGrid.registrationFee)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-3 text-right text-sm">
                              {typeGrid ? (
                                <span className="font-medium text-gray-900">
                                  {formatCurrency(typeGrid.tuitionAmount)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-3 text-right text-sm">
                              {typeGrid ? (
                                <span className="font-semibold text-primary-600">
                                  {formatCurrency(typeGrid.tuitionAmount + typeGrid.registrationFee)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            {canManage && (
                              <td className="px-6 py-3 text-center">
                                {/* Edit only at parent level */}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* Expanded content: Campus rows (when showing all campuses) */}
                  {isExpanded && hasCampusPrices && !differsByNiveau && !differsByTypeFormation && (
                    <>
                      {filiereCampuses.map((campus) => {
                        const campusGrids = filiere.grids.filter((g) => g.campusId === campus.id);
                        const campusPrice = campusGrids.find((g) => g.tuitionAmount > 0 || g.registrationFee > 0) || campusGrids[0];
                        const campusKey = `${filiere.id}-campus-${campus.id}`;
                        const isCampusExpanded = expandedCampuses.has(campusKey);
                        // Check if this campus has type variation (for nested expansion)
                        const hasCampusTypePrices = differsByTypeFormation && campusGrids.length > 1;

                        return (
                          <React.Fragment key={campusKey}>
                            <tr
                              className={`bg-blue-50/30 hover:bg-blue-50/50 transition-colors ${
                                hasCampusTypePrices ? 'cursor-pointer' : ''
                              }`}
                              onClick={() => hasCampusTypePrices && toggleCampus(campusKey)}
                            >
                              <td className="px-6 py-3">
                                <div className="flex items-center gap-3 pl-12">
                                  {hasCampusTypePrices ? (
                                    <div className="w-5 h-5 flex items-center justify-center">
                                      {isCampusExpanded ? (
                                        <ChevronDown className="w-3 h-3 text-gray-400" />
                                      ) : (
                                        <ChevronRight className="w-3 h-3 text-gray-400" />
                                      )}
                                    </div>
                                  ) : (
                                    <div className="w-5" />
                                  )}
                                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <span className="font-medium text-gray-700 text-sm">
                                    {campus.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-3 text-right text-sm">
                                {campusPrice ? (
                                  <span className="text-gray-600">
                                    {formatCurrency(campusPrice.registrationFee)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-3 text-right text-sm">
                                {campusPrice ? (
                                  <span className="font-medium text-gray-900">
                                    {formatCurrency(campusPrice.tuitionAmount)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-3 text-right text-sm">
                                {campusPrice ? (
                                  <span className="font-semibold text-primary-600">
                                    {formatCurrency(campusPrice.tuitionAmount + campusPrice.registrationFee)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              {canManage && (
                                <td className="px-6 py-3 text-center">
                                  {/* Edit only at parent level */}
                                </td>
                              )}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}

                  {/* Expanded content: Campus rows with niveau/type breakdown (when all dimensions vary) */}
                  {isExpanded && hasCampusPrices && (differsByNiveau || differsByTypeFormation) && (
                    <>
                      {filiereCampuses.map((campus) => {
                        const campusGrids = filiere.grids.filter((g) => g.campusId === campus.id);
                        const campusKey = `${filiere.id}-campus-${campus.id}`;
                        const isCampusExpanded = expandedCampuses.has(campusKey);

                        return (
                          <React.Fragment key={campusKey}>
                            <tr
                              className="bg-blue-50/30 hover:bg-blue-50/50 transition-colors cursor-pointer"
                              onClick={() => toggleCampus(campusKey)}
                            >
                              <td className="px-6 py-3">
                                <div className="flex items-center gap-3 pl-12">
                                  <div className="w-5 h-5 flex items-center justify-center">
                                    {isCampusExpanded ? (
                                      <ChevronDown className="w-3 h-3 text-gray-400" />
                                    ) : (
                                      <ChevronRight className="w-3 h-3 text-gray-400" />
                                    )}
                                  </div>
                                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <span className="font-medium text-gray-700 text-sm">
                                    {campus.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-3 text-center text-sm text-gray-500" colSpan={3}>
                                Cliquez pour voir les détails
                              </td>
                              {canManage && (
                                <td className="px-6 py-3 text-center">
                                  {/* Edit only at parent level */}
                                </td>
                              )}
                            </tr>

                            {/* Nested: Niveau rows under campus */}
                            {isCampusExpanded && differsByNiveau && (
                              <>
                                {Array.from({ length: filiere.nombreAnnees }, (_, i) => i + 1).map((niveau) => {
                                  const niveauGrids = campusGrids.filter((g) => g.niveau === niveau);
                                  const niveauKey = `${campusKey}-niveau-${niveau}`;
                                  const isNiveauExpanded = expandedNiveaux.has(niveauKey);
                                  // Always expandable when type formation varies
                                  const hasTypePrices = differsByTypeFormation;
                                  const niveauPrice = niveauGrids.find((g) => g.tuitionAmount > 0 || g.registrationFee > 0) || niveauGrids[0];

                                  return (
                                    <React.Fragment key={niveauKey}>
                                      <tr
                                        className={`bg-purple-50/20 hover:bg-purple-50/40 transition-colors ${
                                          hasTypePrices ? 'cursor-pointer' : ''
                                        }`}
                                        onClick={() => hasTypePrices && toggleNiveau(niveauKey)}
                                      >
                                        <td className="px-6 py-2">
                                          <div className="flex items-center gap-3 pl-20">
                                            {hasTypePrices ? (
                                              <div className="w-4 h-4 flex items-center justify-center">
                                                {isNiveauExpanded ? (
                                                  <ChevronDown className="w-3 h-3 text-gray-400" />
                                                ) : (
                                                  <ChevronRight className="w-3 h-3 text-gray-400" />
                                                )}
                                              </div>
                                            ) : (
                                              <div className="w-4" />
                                            )}
                                            <div className="w-6 h-6 rounded bg-purple-100 flex items-center justify-center">
                                              <Layers className="w-3 h-3 text-purple-600" />
                                            </div>
                                            <span className="text-gray-600 text-sm">
                                              {formatNiveau(niveau)}
                                            </span>
                                          </div>
                                        </td>
                                        {!hasTypePrices ? (
                                          <>
                                            <td className="px-6 py-2 text-right text-sm">
                                              {niveauPrice ? (
                                                <span className="text-gray-600">
                                                  {formatCurrency(niveauPrice.registrationFee)}
                                                </span>
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </td>
                                            <td className="px-6 py-2 text-right text-sm">
                                              {niveauPrice ? (
                                                <span className="font-medium text-gray-900">
                                                  {formatCurrency(niveauPrice.tuitionAmount)}
                                                </span>
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </td>
                                            <td className="px-6 py-2 text-right text-sm">
                                              {niveauPrice ? (
                                                <span className="font-semibold text-primary-600">
                                                  {formatCurrency(niveauPrice.tuitionAmount + niveauPrice.registrationFee)}
                                                </span>
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </td>
                                          </>
                                        ) : (
                                          <td className="px-6 py-2 text-center text-sm text-gray-500" colSpan={3}>
                                            Cliquez pour voir les détails
                                          </td>
                                        )}
                                        {canManage && (
                                          <td className="px-6 py-2 text-center">
                                            <div className="w-6" />
                                          </td>
                                        )}
                                      </tr>

                                      {/* Type formation rows under niveau */}
                                      {isNiveauExpanded && hasTypePrices && (
                                        <>
                                          {(['INITIALE', 'CONTINUE'] as TypeFormation[]).map((type) => {
                                            // Find grid with actual price, or fallback to any matching grid
                                            const typeGrid = niveauGrids.find((g) => g.typeFormation === type && (g.tuitionAmount > 0 || g.registrationFee > 0))
                                              || niveauGrids.find((g) => g.typeFormation === type);
                                            return (
                                              <tr
                                                key={`${niveauKey}-${type}`}
                                                className="bg-green-50/20 hover:bg-green-50/40 transition-colors"
                                              >
                                                <td className="px-6 py-2">
                                                  <div className="flex items-center gap-3 pl-28">
                                                    <div className="w-5 h-5 rounded bg-green-100 flex items-center justify-center">
                                                      <GraduationCap className="w-3 h-3 text-green-600" />
                                                    </div>
                                                    <span className="text-gray-600 text-xs">
                                                      {formatTypeFormation(type)}
                                                    </span>
                                                  </div>
                                                </td>
                                                <td className="px-6 py-2 text-right text-sm">
                                                  {typeGrid ? (
                                                    <span className="text-gray-600">
                                                      {formatCurrency(typeGrid.registrationFee)}
                                                    </span>
                                                  ) : (
                                                    <span className="text-gray-400">-</span>
                                                  )}
                                                </td>
                                                <td className="px-6 py-2 text-right text-sm">
                                                  {typeGrid ? (
                                                    <span className="font-medium text-gray-900">
                                                      {formatCurrency(typeGrid.tuitionAmount)}
                                                    </span>
                                                  ) : (
                                                    <span className="text-gray-400">-</span>
                                                  )}
                                                </td>
                                                <td className="px-6 py-2 text-right text-sm">
                                                  {typeGrid ? (
                                                    <span className="font-semibold text-primary-600">
                                                      {formatCurrency(typeGrid.tuitionAmount + typeGrid.registrationFee)}
                                                    </span>
                                                  ) : (
                                                    <span className="text-gray-400">-</span>
                                                  )}
                                                </td>
                                                {canManage && (
                                                  <td className="px-6 py-2 text-center">
                                                    <div className="w-6" />
                                                  </td>
                                                )}
                                              </tr>
                                            );
                                          })}
                                        </>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </>
                            )}

                            {/* Nested: Type rows under campus (when only type varies) */}
                            {isCampusExpanded && differsByTypeFormation && !differsByNiveau && (
                              <>
                                {(['INITIALE', 'CONTINUE'] as TypeFormation[]).map((type) => {
                                  // Find grid with actual price, or fallback to any matching grid
                                  const typeGrid = campusGrids.find((g) => g.typeFormation === type && (g.tuitionAmount > 0 || g.registrationFee > 0))
                                    || campusGrids.find((g) => g.typeFormation === type);
                                  return (
                                    <tr
                                      key={`${campusKey}-${type}`}
                                      className="bg-green-50/20 hover:bg-green-50/40 transition-colors"
                                    >
                                      <td className="px-6 py-2">
                                        <div className="flex items-center gap-3 pl-20">
                                          <div className="w-4" />
                                          <div className="w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                                            <GraduationCap className="w-3 h-3 text-green-600" />
                                          </div>
                                          <span className="text-gray-600 text-sm">
                                            {formatTypeFormation(type)}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-6 py-2 text-right text-sm">
                                        {typeGrid ? (
                                          <span className="text-gray-600">
                                            {formatCurrency(typeGrid.registrationFee)}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-6 py-2 text-right text-sm">
                                        {typeGrid ? (
                                          <span className="font-medium text-gray-900">
                                            {formatCurrency(typeGrid.tuitionAmount)}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-6 py-2 text-right text-sm">
                                        {typeGrid ? (
                                          <span className="font-semibold text-primary-600">
                                            {formatCurrency(typeGrid.tuitionAmount + typeGrid.registrationFee)}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      {canManage && (
                                        <td className="px-6 py-2 text-center">
                                          <div className="w-6" />
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}

                  {/* Expanded content: Options rows (if filière has options) */}
                  {isExpanded && hasOptions && !differsByNiveau && !differsByTypeFormation && !hasCampusPrices && (
                    <>
                      {filiere.options.map((option) => (
                        <tr
                          key={option.id}
                          className="bg-purple-50/30 hover:bg-purple-50/50 transition-colors"
                        >
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3 pl-12">
                              <div className="w-2 h-8 border-l-2 border-b-2 border-purple-300 rounded-bl" />
                              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <GitBranch className="w-4 h-4 text-purple-600" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-800 text-sm">
                                  {option.optionName || option.filiere}
                                </div>
                                <div className="text-xs text-purple-600">
                                  Option - {option.codeFiliere}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-right text-sm">
                            <span className="text-gray-400">Même prix</span>
                          </td>
                          <td className="px-6 py-3 text-right text-sm">
                            <span className="text-gray-400">-</span>
                          </td>
                          <td className="px-6 py-3 text-right text-sm">
                            <span className="text-gray-400">-</span>
                          </td>
                          {canManage && (
                            <td className="px-6 py-3 text-center">
                              <span className="text-xs text-gray-400">Hérite du parent</span>
                            </td>
                          )}
                        </tr>
                      ))}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TuitionGridGroupedTable;
